import {
  ICompetitionBoard,
  ICompetitionMatches,
} from "@sky-it-livedata/livedata-sports-design-system";
import { slugify } from "../../../utils/string";
import { ErrorName, WidgetError } from "../../../utils/errors";
import { isValidCompetitionBoard } from "./typeguards";
import {
  CreateCompetitionBoardInputSchema,
  CompetitionBoardPhaseRound,
  CompetitionBoardTeam
} from "appsync";
import { CompetitionBoardQuery, MatchPhaseRound } from "../types";
import { intcmpMap } from "../../../utils/intcmp";

const getCta = (params: {
  customCompetitionName: string;
  pageSlug: string;
}): { url: string; label: string } => {
  const { customCompetitionName, pageSlug } = params;

  return {
    url: `/calcio/${slugify(customCompetitionName)}/${pageSlug}`,
    label: "calendar.results.cta",
  };
};

const getRowObject = (
  match: CompetitionBoardPhaseRound,
  matchList: CompetitionBoardPhaseRound[]
): ICompetitionMatches => {
  return {
    link: match?.link || "",
    title: match.date!,
    home: tranformTeamData(match.home!, matchList),
    away: tranformTeamData(match.away!, matchList),
    winner: getWinner(match),
    status: match.status as ICompetitionMatches["status"],
    linkedMatch: match!.linkedMatch ? {
      date: match!.linkedMatch?.date!,
      link: match!.linkedMatch?.link!,
      status: match!.linkedMatch?.status as ICompetitionMatches["status"],
      home: {
        goal: match!.linkedMatch?.home!.goal!,
        name: match!.linkedMatch?.home!.name,
        shortName: match!.linkedMatch?.home!.shortName,
      },
      competitionPhaseRound: match!.linkedMatch?.competitionPhaseRound as MatchPhaseRound,
      away: {
        goal: match!.linkedMatch?.away!.goal!,
        name: match!.linkedMatch?.away!.name,
        shortName: match!.linkedMatch?.away!.shortName,
      },
    }: undefined,
  };
};
const getCurrentPhase = (board: ICompetitionBoard["board"]): number => {
  const phases: Array<keyof ICompetitionBoard["board"]> = [
    "eights",
    "quarters",
    "semi",
    "final",
  ];
  const completeStatuses: Set<CompetitionBoardPhaseRound["status"]> = new Set([
    "FullTime",
    "FullTimeAfterOverTime",
    "FullTimeAfterShootOut",
  ]);

  for (const phase of phases) {
    // eslint-disable-next-line functional/no-let
    let allGamesComplete = true;

    for (const game of board[phase]) {
      if (!completeStatuses.has(game.status)) {
        allGamesComplete = false;
        break;
      }
    }

    if (!allGamesComplete) {
      return phases.indexOf(phase);
    }
  }

  return phases.length - 1;
};

const getWinner = (match: CompetitionBoardPhaseRound) => {
  if (!match.competitionPhaseWinnerTeamId) {
    return undefined;
  }

  if (match?.home?.id === match.competitionPhaseWinnerTeamId) {
    return "home";
  }

  return "away";
};

const tranformTeamData = (
  team: CompetitionBoardTeam,
  matchList: CompetitionBoardPhaseRound[]
) => {
  const prevMatch = team.prevMatch
    ? matchList.find((match) => match.id === team.prevMatch)
    : undefined;

  const teamObj: any = {
    id: team?.id || null,
    image: team.logoUrl,
    name: team.name || undefined,
    shortName: team.shortName || undefined,
    goal: team.goal || 0,
    shootoutGoal: team.shootoutGoal || 0,
  };

  teamObj.prevMatch = {
    home: {
      image: prevMatch?.home?.logoUrl || undefined,
      name: prevMatch?.home?.name || undefined,
      shortName: prevMatch?.home?.shortName || undefined,
      goal: prevMatch?.home?.goal || 0,
      shootoutGoal: prevMatch?.home?.shootoutGoal || 0,
    },
    away: {
      image: prevMatch?.away?.logoUrl || undefined,
      name: prevMatch?.away?.name || undefined,
      shortName: prevMatch?.away?.shortName || undefined,
      goal: prevMatch?.away?.goal || 0,
      shootoutGoal: prevMatch?.away?.shootoutGoal || 0,
    },
    winner: prevMatch ? getWinner(prevMatch) : undefined,
  };

  return teamObj;
};

const transformer = (obj: CompetitionBoardQuery): ICompetitionBoard => {
  if (isValidCompetitionBoard(obj)) {
    CreateCompetitionBoardInputSchema().parse(obj?.data?.getCompetitionBoard);
    const {
      matchList,
      boardSponsorLink,
      competitionId,
      customCompetitionName,
    } = obj?.data.getCompetitionBoard;

    const mappedData: ICompetitionBoard = {
      board: {
        eights: [],
        quarters: [],
        semi: [],
        final: [],
      },
      competitionId,
      boardSponsorLink: boardSponsorLink || "",
      trackingData: intcmpMap.competitionBoard,
      cta: getCta({
        customCompetitionName: customCompetitionName!,
        pageSlug: "calendario-risultati",
      }),
    };

    const cleanMatchList =
      matchList?.filter(
        (match): match is CompetitionBoardPhaseRound => match != null
      ) ?? [];

    cleanMatchList.forEach((match) => {
      if (match.competitionPhase === "Ottavi") {
        return mappedData.board.eights.push({
          hidePrevMatches: true,
          ...getRowObject(match, cleanMatchList),
        });
      }

      if (match.competitionPhase === "Quarti") {
        return mappedData.board.quarters.push(
          getRowObject(match, cleanMatchList)
        );
      }

      if (match.competitionPhase === "Semifinali") {
        return mappedData.board.semi.push(getRowObject(match, cleanMatchList));
      }

      if (match.competitionPhase === "Finale") {
        return mappedData.board.final.push(getRowObject(match, cleanMatchList));
      }

      return undefined;
    });

    mappedData.initialVisibleColumn = getCurrentPhase(mappedData.board);
    return mappedData;
  } else {
    throw new WidgetError<ErrorName>({
      name: "NO_VALID_DATA",
      message:
        "Invalid transformer input, Error in CompetitionBoardQuery object",
      data: obj,
    });
  }
};

export default transformer;
